import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import type {
  ArtistProfile,
  PricingSettings,
  SizeSetting,
  ComplexityMultiplier,
  BodyPartMultiplier,
  StyleMultiplier,
  FullPricingConfig,
  DEFAULT_SIZE_SETTINGS,
  DEFAULT_COMPLEXITY_MULTIPLIERS,
  DEFAULT_STYLE_MULTIPLIERS,
  DEFAULT_BODY_PART_MULTIPLIERS,
  SizeCategory,
  ComplexityLevel,
  TattooStyle,
  BodyPart,
} from '@/types/pricing';
import {
  DEFAULT_SIZE_SETTINGS as defaultSizes,
  DEFAULT_COMPLEXITY_MULTIPLIERS as defaultComplexity,
  DEFAULT_STYLE_MULTIPLIERS as defaultStyles,
  DEFAULT_BODY_PART_MULTIPLIERS as defaultBodyParts,
} from '@/types/pricing';

export function useArtistProfile() {
  const { user } = useAuth();
  const { impersonatedArtistId, isImpersonating } = useImpersonation();

  return useQuery({
    queryKey: ['artist-profile', isImpersonating ? impersonatedArtistId : user?.id],
    queryFn: async () => {
      if (isImpersonating && impersonatedArtistId) {
        // Fetch by artist ID directly (super admin has SELECT access)
        const { data, error } = await supabase
          .from('artist_profiles')
          .select('*')
          .eq('id', impersonatedArtistId)
          .maybeSingle();
        if (error) throw error;
        return data as unknown as ArtistProfile;
      }

      if (!user) return null;

      const { data, error } = await supabase
        .from('artist_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return (data as unknown as ArtistProfile) ?? null;
    },
    enabled: isImpersonating ? !!impersonatedArtistId : !!user,
  });
}

export function usePricingSettings(artistId: string | undefined) {
  return useQuery({
    queryKey: ['pricing-settings', artistId],
    queryFn: async () => {
      if (!artistId) return null;

      const { data, error } = await supabase
        .from('pricing_settings')
        .select('*')
        .eq('artist_id', artistId)
        .maybeSingle();

      if (error) throw error;
      return (data as PricingSettings) ?? null;
    },
    enabled: !!artistId,
  });
}

export function useFullPricingConfig(artistId: string | undefined) {
  return useQuery({
    queryKey: ['full-pricing-config', artistId],
    queryFn: async () => {
      if (!artistId) return null;

      // Step 1: Fetch artist + pricing in parallel
      const [artistResult, pricingResult] = await Promise.all([
        supabase.from('artist_profiles').select('*').eq('id', artistId).maybeSingle(),
        supabase.from('pricing_settings').select('*').eq('artist_id', artistId).maybeSingle(),
      ]);

      if (artistResult.error || pricingResult.error) {
        return null;
      }

      const pricingId = pricingResult.data?.id;
      if (!pricingId) return null;

      // Step 2: Fetch all multipliers with correct pricing_settings_id
      const [sizeData, complexityData, bodyPartData, styleData] = await Promise.all([
        supabase.from('size_settings').select('*').eq('pricing_settings_id', pricingId),
        supabase.from('complexity_multipliers').select('*').eq('pricing_settings_id', pricingId),
        supabase.from('body_part_multipliers').select('*').eq('pricing_settings_id', pricingId),
        supabase.from('style_multipliers').select('*').eq('pricing_settings_id', pricingId),
      ]);

      // Apply fixed logical sort orders (prevents UI resorting on edit)
      const BODY_PART_ORDER: Record<string, number> = {
        face: 0, neck: 1, shoulder: 2, chest: 3, back: 4, ribs: 5, stomach: 6, hip: 7,
        upper_arm: 8, arm: 9, forearm: 10, hand: 11, finger: 12,
        thigh: 13, leg: 14, calf: 15, foot: 16, other: 17,
      };
      const COMPLEXITY_ORDER: Record<string, number> = { simple: 0, medium: 1, detailed: 2 };
      const SIZE_ORDER: Record<string, number> = { mini: 0, small: 1, medium: 2, large: 3, xl: 4 };

      const sortedBodyParts = [...(bodyPartData.data || [])].sort(
        (a, b) => (BODY_PART_ORDER[a.body_part] ?? 99) - (BODY_PART_ORDER[b.body_part] ?? 99)
      );
      const sortedComplexity = [...(complexityData.data || [])].sort(
        (a, b) => (COMPLEXITY_ORDER[a.complexity_level] ?? 99) - (COMPLEXITY_ORDER[b.complexity_level] ?? 99)
      );
      const sortedSizes = [...(sizeData.data || [])].sort(
        (a, b) => (SIZE_ORDER[a.size_category] ?? 99) - (SIZE_ORDER[b.size_category] ?? 99)
      );

      return {
        artist: artistResult.data as unknown as ArtistProfile,
        pricing: pricingResult.data as PricingSettings,
        sizeSettings: sortedSizes as SizeSetting[],
        complexityMultipliers: sortedComplexity as ComplexityMultiplier[],
        bodyPartMultipliers: sortedBodyParts as BodyPartMultiplier[],
        styleMultipliers: (styleData.data || []) as StyleMultiplier[],
      } as FullPricingConfig;
    },
    enabled: !!artistId,
  });
}

export function useCreateInitialSettings() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ artistName }: { artistName: string }) => {
      if (!user) throw new Error('Not authenticated');

      // Create artist profile
      const { data: profile, error: profileError } = await supabase
        .from('artist_profiles')
        .insert({
          user_id: user.id,
          artist_name: artistName,
        })
        .select()
        .single();

      if (profileError) throw profileError;

      // Create pricing settings
      const { data: pricing, error: pricingError } = await supabase
        .from('pricing_settings')
        .insert({
          artist_id: profile.id,
        })
        .select()
        .single();

      if (pricingError) throw pricingError;

      // Create all default multipliers in parallel
      await Promise.all([
        supabase.from('size_settings').insert(
          defaultSizes.map(s => ({ ...s, pricing_settings_id: pricing.id }))
        ),
        supabase.from('complexity_multipliers').insert(
          defaultComplexity.map(c => ({ ...c, pricing_settings_id: pricing.id }))
        ),
        supabase.from('style_multipliers').insert(
          defaultStyles.map(s => ({ ...s, pricing_settings_id: pricing.id }))
        ),
        supabase.from('body_part_multipliers').insert(
          defaultBodyParts.map(b => ({ ...b, pricing_settings_id: pricing.id }))
        ),
      ]);

      return { profile, pricing };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artist-profile'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-settings'] });
    },
  });
}

export function useUpdatePricingSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PricingSettings> & { id: string }) => {
      const { data, error } = await supabase
        .from('pricing_settings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricing-settings', data.artist_id] });
      queryClient.invalidateQueries({ queryKey: ['full-pricing-config'] });
    },
  });
}

export function useUpdateSizeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SizeSetting> & { id: string }) => {
      const { data, error } = await supabase
        .from('size_settings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['full-pricing-config'] });
    },
  });
}

export function useUpdateMultiplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      table, 
      id, 
      multiplier 
    }: { 
      table: 'complexity_multipliers' | 'body_part_multipliers' | 'style_multipliers';
      id: string;
      multiplier: number;
    }) => {
      const { data, error } = await supabase
        .from(table)
        .update({ multiplier })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['full-pricing-config'] });
    },
  });
}

export function useToggleStyleActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('style_multipliers')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['full-pricing-config'] });
    },
  });
}

export function useUpdateArtistTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      artistId, 
      theme_primary_hue,
      theme_primary_saturation,
      theme_primary_lightness,
    }: { 
      artistId: string;
      theme_primary_hue: number;
      theme_primary_saturation: number;
      theme_primary_lightness: number;
    }) => {
      const { data, error } = await supabase
        .from('artist_profiles')
        .update({ 
          theme_primary_hue,
          theme_primary_saturation,
          theme_primary_lightness,
        })
        .eq('id', artistId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artist-profile'] });
      queryClient.invalidateQueries({ queryKey: ['full-pricing-config'] });
    },
  });
}

export function useFirstArtist() {
  return useQuery({
    queryKey: ['first-artist'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('artist_profiles')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as ArtistProfile | null;
    },
  });
}

export function useArtistBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['artist-by-slug', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('artist_profiles')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as ArtistProfile | null;
    },
    enabled: !!slug,
    retry: false,
    staleTime: 60_000,
  });
}

export function useArtistByCode(code: string | undefined) {
  return useQuery({
    queryKey: ['artist-by-code', code],
    queryFn: async () => {
      if (!code) return null;
      const { data, error } = await supabase
        .from('artist_profiles')
        .select('*')
        .eq('calculator_code', code.toUpperCase())
        .maybeSingle();

      if (error) throw error;
      return data as unknown as ArtistProfile | null;
    },
    enabled: !!code,
  });
}
